"use client";

import type { RouterOutputs } from "@api/trpc/routers/_app";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { DeleteModal } from "@/components/modals/delete-modal";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeleteStudyset } from "@/features/studysets/api/use-delete-studyset";
import { useToggleSaveStudySet } from "@/features/studysets/api/use-toggle-save-studyset";
import { getSlug } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import StudySetDropDown from "./studyset-dropdown";

interface StudySetCardProps {
  studySet: RouterOutputs["studysets"]["getPublic"]["results"][0];
  isPublic: boolean;
  folderId?: string;
}

export const StudySetCard = ({
  studySet,
  folderId,
  isPublic = true,
}: StudySetCardProps) => {
  const studySetUrl = `/studysets/${getSlug(studySet.name, studySet.id)}`;
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const studySetId = studySet.id;
  const deleteMutation = useDeleteStudyset({ id: studySetId });
  const unLikeMutation = useToggleSaveStudySet({ id: studySet.id });
  const _router = useRouter();
  const t = useI18n();

  const handleUnlike = () => {
    setIsDeleteOpen(false);
    unLikeMutation.mutate({ id: studySet.id, isLiked: true });
  };

  return (
    <>
      <Link href={studySetUrl} className="group">
        <Card className="flex flex-col rounded-2xl border bg-muted/40 h-[180px]">
          <CardContent className="flex-grow py-6 px-4">
            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-base truncate">
                  {studySet.name}
                </h3>
                {!isPublic && (
                  <StudySetDropDown
                    studySet={studySet}
                    setIsDeleteOpen={setIsDeleteOpen}
                    folderId={folderId}
                  />
                )}
              </div>
              <p className="text-xs font-normal text-muted-foreground">
                {t("studySet.card.by")} {studySet.author}
              </p>
            </div>
          </CardContent>
          <CardFooter className="p-4 flex justify-between items-center">
            <p className="text-xs font-normal text-muted-foreground">
              {studySet.questionCount} {t("studySet.card.questions")}
            </p>
            <p className="text-[10px] leading-4 font-bold text-brand-primary truncate max-w-20 uppercase">
              {studySet.subject ?? t("studySet.card.various")}
            </p>
          </CardFooter>
        </Card>
      </Link>
      {studySet.type !== "public" && (
        <DeleteModal
          title={t("studySet.card.delete.title")}
          message={
            studySet.type === "personal"
              ? t("studySet.card.delete.description")
              : t("studySet.card.delete.unsave")
          }
          handleCancel={() => setIsDeleteOpen(false)}
          handleConfirm={() => {
            if (studySet.type === "personal") {
              deleteMutation.mutate(
                { id: studySetId },
                {
                  onSuccess: () => setIsDeleteOpen(false),
                },
              );
            } else {
              handleUnlike();
            }
          }}
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          pending={deleteMutation.isPending}
        />
      )}
    </>
  );
};

export function StudySetCardsSkeleton({
  count,
  cols,
}: {
  count: number;
  cols: number;
}) {
  return (
    <div className={`grid md:grid-cols-2 lg:grid-cols-${cols} gap-4`}>
      {[...Array(count)].map((_, index) => (
        <Card
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder, no stable id available
          key={`studyset-skeleton-${index}`}
          className="flex flex-col rounded-2xl border bg-muted/40 h-[180px]"
        >
          <CardContent className="flex-grow py-6 px-4">
            <div className="flex flex-col gap-2">
              {/* Skeleton for the title */}
              <Skeleton className="h-5 w-3/4" />
              {/* Skeleton for the author */}
              <Skeleton className="h-3 w-24" />
            </div>
          </CardContent>

          <CardFooter className="p-4 flex justify-between items-center">
            {/* Skeleton for question count */}
            <Skeleton className="h-3 w-24" />
            {/* Skeleton for subject */}
            <Skeleton className="h-3 w-16" />
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}
